import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { substituteResolvedValues } from '@inference.engine/taxonomy/web';
import { i18n } from '@/shared/i18n';
import { Trans, useTranslation } from 'react-i18next';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import {
  Badge,
  Button,
  Calendar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRightToLine,
  ArrowUpRight,
  CalendarDays,
  Copy,
  CornerDownRight,
  CornerUpLeft,
  GripVertical,
  Lock,
  MoreVertical,
  Pencil,
  Play,
  ListTree,
  Plus,
  RotateCcw,
  Settings2,
  Trash2,
  Unlink,
} from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import { useMountEffect } from '@/shared/hooks/useMountEffect';
import {
  canBindList,
  fieldPatternViolation,
  clampFieldValue,
  formatDateMask,
  MAX_CELL_CODE_LENGTH,
  MAX_CELL_VALUE_LENGTH,
  TAXONOMY_FIELD_TYPE_LABELS,
  taxonomiesQueryOptions,
  taxonomyLevelLabel,
  useGenerateTrackingCode,
  type TaxonomyField,
  type TaxonomyFieldGroup,
  type TaxonomyFieldType,
  type TaxonomyFieldValue,
} from '@/entities/taxonomy';
import { AddFieldModal } from '../AddFieldModal/AddFieldModal';
import { EditFieldModal } from '../EditFieldModal/EditFieldModal';
import { TrackingCodeComposer } from '../TrackingCodeComposer/TrackingCodeComposer';
import { LinkedSchemaComposer } from '../LinkedSchemaComposer/LinkedSchemaComposer';
import { LinkListModal } from '../LinkListModal/LinkListModal';
import type { ResolvedList } from '@/entities/taxonomy-list';
import { applyFieldPatch } from '../../lib/applyFieldPatch';
import { cellKey, type SchemaProblem } from '../../lib/validateSchema';
import { getStandardFieldByKey } from '../FieldLibraryModal/standard-fields';
import { patternToString, resolveVariableForRow } from '../../lib/trackingPattern';
import { EMPTY_VALUE, pruneEmptyRows } from '../../lib/pruneEmptyRows';
import { describeLink } from '../../lib/linkedSchema';
import { FormDialog } from '@/shared/ui/FormDialog';
import { ProblemTriangle } from '../../../components/ProblemTriangle/ProblemTriangle';

/** Generated columns (computed Variable + linked join key) have no editable cells. */
const isGeneratedType = (t: TaxonomyFieldType) => t === 'computed' || t === 'linked';

/**
 * A problem the details popover asked the grid to show.
 *
 * `nonce` is what makes the request repeatable: clicking the same problem twice
 * must scroll to it twice, and a prop that compares equal would be a no-op the
 * second time.
 */
export interface FocusTarget {
  fieldId: string;
  /** Absent for a column-level finding — those focus the header, not a row. */
  row?: number;
  column?: 'value' | 'code';
  nonce: number;
}

interface FieldsEditorProps {
  fields: TaxonomyField[];
  readOnly: boolean;
  /** `immediate` marks a batch the user made in one gesture, which is checked without waiting. */
  onChange: (next: TaxonomyField[], opts?: { immediate?: boolean }) => void;
  /** Whether a cell editor or a column rename is open anywhere in the grid. */
  onEditingChange?: (open: boolean) => void;
  /** Taxonomy + version ids — needed to generate tracking-code samples. */
  taxonomyId: string;
  versionId: string;
  /**
   * Vocabularies resolved from shared lists, keyed by field id. Rendered for
   * list-bound columns; `field.values` remains the authoritative persisted
   * state so autosave never writes a list's options into the schema.
   */
  resolvedLists: Record<string, ResolvedList>;
  /** Codes this schema has issued, for the composer's edit warning. */
  mintedCount?: number;
  /**
   * Validate findings anchored to a single cell, keyed by {@link cellKey}.
   *
   * Deliberately fed into the SAME `violation` channel the live
   * `validationPattern` check already uses — one red tint, one triangle, one
   * tooltip. A second visual language for "this cell is wrong" would leave the
   * grid with two reds meaning different things.
   */
  cellProblems?: Map<string, SchemaProblem[]>;
  /** Validate findings anchored to a whole column, keyed by field id. */
  fieldProblems?: Map<string, SchemaProblem[]>;
  /**
   * A cell or column the details popover wants revealed.
   *
   * Needed because the grid windows past 50 rows: a finding below the fold has
   * no DOM node to hover, so the summary used to tell people to hover something
   * that was not there. Routed by the field's group — each group table owns its
   * own scroller and selection.
   */
  focusTarget?: FocusTarget | null;
}

/** A dialog or menu action rewrites a column in one gesture, so it is checked at once. */
const IMMEDIATE = { immediate: true };

/**
 * Reports an editor opening and closing, including an unmount while it is open.
 *
 * A table or header can unmount mid-edit (a tab switch, or a reseed that drops
 * its field) without ever committing, and an unreported close holds every idle
 * check for the rest of the session.
 */
function useReportedEditorOpen(report: (open: boolean) => void) {
  const openRef = useRef(false);
  const reportRef = useRef(report);
  reportRef.current = report;
  useMountEffect(() => () => {
    if (openRef.current) reportRef.current(false);
  });
  return (open: boolean) => {
    openRef.current = open;
    report(open);
  };
}

/** One tooltip line per problem: what's wrong, then what to do. */
function describeProblems(problems: SchemaProblem[] | undefined): string | null {
  if (!problems?.length) return null;
  return problems.map((p) => (p.hint ? `${p.message}\n${p.hint}` : p.message)).join('\n\n');
}

/**
 * Merges the always-on pattern check with the on-demand Validate findings.
 *
 * `value_breaks_pattern` is ALWAYS dropped from the snapshot: the live check is
 * the authority on that particular fact, and it re-evaluates the value in the
 * cell right now. Filtering it only while the live check happens to be firing
 * meant that fixing the value made the stale message reappear — the tooltip
 * then quoted a value the cell no longer held.
 */
function mergeViolation(live: string | null, problems: SchemaProblem[] | undefined): string | null {
  const rest = problems?.filter((p) => p.code !== 'value_breaks_pattern');
  return [live, describeProblems(rest)].filter(Boolean).join('\n\n') || null;
}

/**
 * How loudly a cell or column should be painted. Errors win over warnings, and
 * the live pattern check is always an error — the API rejects that value.
 *
 * Without this every finding rendered destructive-red, so a duplicate display
 * value looked exactly like a missing code and the summary bar's "2 warnings,
 * 0 errors" contradicted the grid underneath it.
 */
function violationSeverityOf(
  live: string | null,
  problems: SchemaProblem[] | undefined,
): 'error' | 'warning' | null {
  if (live) return 'error';
  if (!problems?.length) return null;
  return problems.some((p) => p.severity === 'error') ? 'error' : 'warning';
}

// Getters, not values: a resolved label would freeze at import.
const GROUP_META: Record<TaxonomyFieldGroup, { label: string; accent: string; dot: string }> = {
  media: {
    get label() {
      return i18n.t('knowledge:taxonomy.fieldsEditor.mediaFields');
    },
    accent: 'border-t-sky-400',
    dot: 'bg-sky-400',
  },
  content: {
    get label() {
      return i18n.t('knowledge:taxonomy.fieldsEditor.contentFields');
    },
    accent: 'border-t-amber-400',
    dot: 'bg-amber-400',
  },
};

const DEFAULT_VALUE_WIDTH = 128;
const DEFAULT_CODE_WIDTH = 72;
const MIN_COL_WIDTH = 48;

/**
 * Floor for a group's width, driven by its HEADER rather than its columns.
 *
 * One field is only 200px of columns (128 + 72), which isn't enough for
 * "● Content fields ⓵ + Add field" — the label wrapped to two lines and the
 * button ended up against the edge. Measured in the app, that header needs
 * 270px: 146 label + badge, 85 button, 16 gap, 24 cell padding. Rounded up for
 * breathing room so the button isn't flush against the border either.
 *
 * The same floor applies to the empty state, so two groups stay the same width
 * when one has a single field and the other has none.
 */
const MIN_GROUP_WIDTH = 288;

type Sub = 'value' | 'code';
interface Selection {
  ar: number;
  ac: number;
  fr: number;
  fc: number;
}

/**
 * Spreadsheet grid editor for a schema's fields (SME: left-to-right table).
 * Media fields and Content fields render as two separate, accent-topped tables
 * sitting side-by-side with a small gap — Content to the RIGHT of Media, not
 * below. Their rows stay aligned because both are driven by one shared row
 * count; a single "Add row" appends across every field in both groups.
 *
 * Pure controlled component: every edit produces a new `fields` array and calls
 * `onChange`; the parent owns persistence (debounced autosave).
 */
export function FieldsEditor({
  fields,
  readOnly,
  onChange,
  taxonomyId,
  versionId,
  resolvedLists,
  mintedCount,
  cellProblems,
  fieldProblems,
  focusTarget,
  onEditingChange,
}: FieldsEditorProps) {
  const { t } = useTranslation('knowledge');
  const openEditorsRef = useRef(new Set<string>());
  const reportEditing = (key: string, open: boolean) => {
    const openEditors = openEditorsRef.current;
    const wasOpen = openEditors.size > 0;
    if (open) openEditors.add(key);
    else openEditors.delete(key);
    if (openEditors.size > 0 !== wasOpen) onEditingChange?.(!wasOpen);
  };
  const [addGroup, setAddGroup] = useState<TaxonomyFieldGroup | null>(null);
  const [addAfterId, setAddAfterId] = useState<string | null>(null);
  /** When adding a SUBFIELD, the parent field's id (locks the group + "-" join). */
  const [addParentId, setAddParentId] = useState<string | null>(null);
  /** Contextual adds (subfield / add-to-right) skip the library picker. */
  const [addSkipPicker, setAddSkipPicker] = useState(false);
  const [editingField, setEditingField] = useState<TaxonomyField | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  /** Id of the computed field whose pattern composer is open. */
  const [composerFieldId, setComposerFieldId] = useState<string | null>(null);
  /** Id of the linked field whose target composer is open. */
  const [linkedFieldId, setLinkedFieldId] = useState<string | null>(null);
  /** Id of the library field pending a "Reset to default" confirmation. */
  const [resetFieldId, setResetFieldId] = useState<string | null>(null);
  /** Id of the field whose shared-list binding is being configured. */
  const [linkListFieldId, setLinkListFieldId] = useState<string | null>(null);

  // Schema names for linked-field subtitles (id → name). Read-only lookup.
  const { data: schemaList = [] } = useQuery(taxonomiesQueryOptions());
  const linkedSchemaNames: Record<string, string> = Object.fromEntries(
    schemaList.map((s) => [s.id, s.name]),
  );

  const mediaFields = fields.filter((f) => f.group === 'media');
  const contentFields = fields.filter((f) => f.group === 'content');
  const hasAnyField = mediaFields.length > 0 || contentFields.length > 0;

  const generateSample = useGenerateTrackingCode(taxonomyId);
  /** Mint a sample code for a Variable column using each referenced field's first value. */
  const handleGenerateSample = (variable: TaxonomyField) => {
    const refIds = [
      ...new Set(
        (variable.pattern ?? [])
          .filter((p) => p.kind === 'field' && p.fieldId)
          .map((p) => p.fieldId as string),
      ),
    ];
    // Hydrated, like the preview beside it: a column bound at creation has no
    // inline values, so sampling from the raw fields minted from an empty string
    // while the on-screen preview showed a real code.
    const values = refIds.map((fieldId) => ({
      fieldId,
      value: hydratedFields.find((f) => f.id === fieldId)?.values[0]?.value ?? '',
    }));
    generateSample.mutate(
      { fieldId: variable.id, versionId, values },
      {
        onSuccess: (res) => enqueueSnackbar(`Sample: ${res.code}`, { variant: 'success' }),
      },
    );
  };

  /**
   * How many rows the grid renders.
   *
   * A list-bound column draws its cells from the RESOLVED list, not from its
   * inline `values`, so its length has to come from the same place. Measuring
   * only `f.values.length` truncated bound columns to their inline FALLBACK —
   * Language showed ~6 of its 183 entries, and adding rows to some other field
   * made the missing languages "appear" as the row count grew past them. The
   * options were always there; the grid just wasn't tall enough to show them.
   */
  const rowsInField = (f: TaxonomyField) =>
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- resolvedLists only holds entries for list-bound fields
    f.listRef?.listId ? (resolvedLists[f.id]?.values.length ?? f.values.length) : f.values.length;

  const maxLen = fields.reduce((m, f) => Math.max(m, rowsInField(f)), 0);

  /**
   * The same fields with bound columns' vocabularies filled in.
   *
   * A Variable's preview reads its referenced field's `values` row by row, and
   * a bound column's own `values` are the stale inline fallback — so building
   * the preview from the raw fields showed codes for the few inline rows and
   * blanks for every row the shared list actually supplies. The grid already
   * resolves bound columns for its own cells; this makes the Variable read the
   * same vocabulary rather than a different one.
   */
  const hydratedFields = useMemo(
    () => substituteResolvedValues(fields, resolvedLists),
    [fields, resolvedLists],
  );

  // ─── Row virtualization ───────────────────────────────────────────────────
  // Both group tables share this scroller and the same row count, so a single
  // virtualizer drives both — they render identical spacer/visible windows and
  // stay row-aligned. Only kicks in past a threshold; smaller schemas render
  // every row exactly as before (windowing adds nothing there). Fixed row
  // height (no measureElement) keeps the two tables in lockstep.
  const ROW_HEIGHT = 37; // h-9 cell (36px) + 1px bottom border
  const VIRTUALIZE_THRESHOLD = 50;
  const shouldVirtualize = maxLen > VIRTUALIZE_THRESHOLD;
  const boardScrollRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  // Rows sit below the sticky <thead>; scrollMargin = that offset so the
  // virtualizer's row coordinates line up with the scroller. Measured off the
  // first tbody; both tables' bodies start at the same Y.
  useEffect(() => {
    const scroller = boardScrollRef.current;
    if (!scroller || !shouldVirtualize) return;
    const measure = () => {
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- `tbody` is a current tag; the rule cannot pin the non-deprecated overload
      const tbody = scroller.querySelector<HTMLTableSectionElement>('tbody');
      if (!tbody) return;
      const next =
        tbody.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop;
      setScrollMargin((prev) => (Math.abs(prev - next) > 0.5 ? next : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scroller);
    return () => ro.disconnect();
  }, [shouldVirtualize, fields.length, maxLen]);

  const rowVirtualizer = useVirtualizer({
    count: maxLen,
    getScrollElement: () => boardScrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
    scrollMargin,
    enabled: shouldVirtualize,
  });

  // The windowed slice + spacer heights, shared by both group tables (null when
  // not virtualizing → tables render all rows).
  const virtualItems = rowVirtualizer.getVirtualItems();
  const rowWindow =
    shouldVirtualize && virtualItems.length > 0
      ? {
          indexes: virtualItems.map((v) => v.index),
          paddingTop: Math.max(0, virtualItems[0].start - scrollMargin),
          paddingBottom: Math.max(
            0,
            rowVirtualizer.getTotalSize() -
              (virtualItems[virtualItems.length - 1].end - scrollMargin),
          ),
        }
      : null;

  /**
   * Scrolls a row the window never rendered into view.
   *
   * Only reachable for an off-window row, because a mounted cell is scrolled by
   * the DOM directly (see `revealCell` in GroupTable) — which also handles the
   * horizontal axis the virtualizer knows nothing about. `scrollToIndex` is a
   * hard no-op while `enabled` is false: with no measurements there is no
   * offset to compute, so the DOM path is the only one that works below the
   * threshold, not merely the cheaper one.
   */
  const scrollRowIntoView = (row: number) => {
    if (shouldVirtualize) rowVirtualizer.scrollToIndex(row, { align: 'auto' });
  };

  /** Delete a field AND (if it's a parent) its subfields — one level deep. */
  const removeField = (id: string) =>
    onChange(fields.filter((f) => f.id !== id && f.parentId !== id));
  const patchField = (id: string, patch: Partial<TaxonomyField>, opts?: { immediate?: boolean }) =>
    onChange(applyFieldPatch(fields, id, patch), opts);

  /** Id of the last field in `id`'s cluster (the field + any of its subfields,
   *  which are kept adjacent right after it). For a subfield, returns itself. */
  const clusterEndId = (id: string): string => {
    const start = fields.findIndex((f) => f.id === id);
    if (start === -1) return id;
    let end = start;
    for (let j = start + 1; j < fields.length; j++) {
      if (fields[j].parentId === id) end = j;
      else break;
    }
    return fields[end].id;
  };

  /** Insert `newField` right after `afterId` in the flat field order. */
  const insertAfter = (afterId: string, newField: TaxonomyField) => {
    const idx = fields.findIndex((f) => f.id === afterId);
    if (idx === -1) {
      onChange([...fields, newField]);
      return;
    }
    const next = [...fields];
    next.splice(idx + 1, 0, newField);
    onChange(next);
  };

  const handleAdd = (field: TaxonomyField) => {
    const nf = addParentId ? { ...field, parentId: addParentId } : field;
    if (addAfterId) insertAfter(addAfterId, nf);
    else onChange([...fields, nf]);
  };

  const duplicateField = (field: TaxonomyField) =>
    insertAfter(clusterEndId(field.id), {
      ...field,
      id: crypto.randomUUID(),
      name: `${field.name} copy`,
      parentId: field.parentId ?? null,
      libraryKey: field.libraryKey ?? null,
      values: field.values.map((v) => ({ ...v })),
    });

  /**
   * Unbinds a field from its shared list. The inline `values` were never
   * overwritten while bound, so the column simply returns to whatever was
   * typed before — no data recovery needed.
   */
  const unlinkFromList = (id: string) => patchField(id, { listRef: undefined }, IMMEDIATE);

  /** Restore a library field's values/format to its catalog defaults (destructive). */
  const resetFieldToDefault = (id: string) => {
    const field = fields.find((f) => f.id === id);
    const def = getStandardFieldByKey(field?.libraryKey);
    if (!field || !def) return;
    patchField(
      id,
      { values: def.values.map((v) => ({ ...v })), format: def.format ?? null },
      IMMEDIATE,
    );
    setResetFieldId(null);
  };

  /** ⋮ "Add field to the right" — a same-level sibling of `field` (opens the library picker). */
  const addSiblingAfter = (field: TaxonomyField) => {
    setAddParentId(field.parentId ?? null);
    // subfield sibling → right after it; top-level → after its whole cluster
    setAddAfterId(field.parentId ? field.id : clusterEndId(field.id));
    setAddGroup(field.group);
    setAddSkipPicker(false);
  };

  /** ⋮ "Add subfield" — a child of `field`, appended after its existing subfields (library picker). */
  const addSubfield = (field: TaxonomyField) => {
    setAddParentId(field.id);
    setAddAfterId(clusterEndId(field.id));
    setAddGroup(field.group);
    setAddSkipPicker(false);
  };

  /**
   * ⋮ "Detach from parent" — promotes a subfield to a field of its own.
   *
   * The way out of a cluster. A subfield's section is locked to its parent and
   * its name joins with the subfield delimiter, so without this the only escape
   * was deleting it and building it again. It keeps its group and stays where it
   * sits, so it lands immediately after the cluster it left — no reshuffle, and
   * the "_" it now joins with is visible in the preview straight away.
   */
  const detachField = (id: string) => {
    onChange(fields.map((f) => (f.id === id ? { ...f, parentId: null } : f)));
  };

  const closeAddModal = () => {
    setAddGroup(null);
    setAddAfterId(null);
    setAddParentId(null);
    setAddSkipPicker(false);
  };

  const setCell = (fieldId: string, row: number, patch: Partial<TaxonomyFieldValue>) =>
    onChange(
      pruneEmptyRows(
        fields.map((f) => {
          if (f.id !== fieldId) return f;
          const values = [...f.values];
          while (values.length <= row) values.push({ ...EMPTY_VALUE });
          values[row] = { ...values[row], ...patch };
          return { ...f, values };
        }),
      ),
    );

  /** Apply MANY cell writes in a single update (copy/paste, fill, clear). Doing
   *  these as N separate setCell calls loses all but the last (each derives from
   *  the same stale `fields`), so batch them here. */
  const setCells = (
    writes: { fieldId: string; row: number; sub: 'value' | 'code'; value: string }[],
  ) => {
    if (writes.length === 0) return;
    onChange(
      pruneEmptyRows(
        fields.map((f) => {
          const fw = writes.filter((w) => w.fieldId === f.id);
          if (fw.length === 0) return f;
          const values = f.values.map((v) => ({ ...v }));
          for (const w of fw) {
            while (values.length <= w.row) values.push({ ...EMPTY_VALUE });
            values[w.row] = { ...values[w.row], [w.sub]: w.value };
          }
          return { ...f, values };
        }),
      ),
      { immediate: true },
    );
  };

  /** Reorder columns via drag. Moves the dragged field to the drop target, then
   *  normalizes so subfields stay adjacent to their parent (a top-level drag
   *  carries its cluster; a subfield can only reorder within its parent). */
  const reorderFields = (activeId: string, overId: string) => {
    const from = fields.findIndex((f) => f.id === activeId);
    const to = fields.findIndex((f) => f.id === overId);
    if (from < 0 || to < 0 || from === to) return;
    const moved = arrayMove(fields, from, to);
    // Re-group: each top-level field (in the moved order) followed by its subfields.
    const result: TaxonomyField[] = [];
    const placed = new Set<string>();
    for (const f of moved) {
      if (f.parentId) continue;
      result.push(f);
      placed.add(f.id);
      for (const sub of moved.filter((s) => s.parentId === f.id)) {
        result.push(sub);
        placed.add(sub.id);
      }
    }
    // Safety: append any orphaned subfields (parent missing) so none are lost.
    for (const f of moved) if (!placed.has(f.id)) result.push(f);
    onChange(result);
  };

  /** Append one empty row across EVERY field (a spreadsheet row add). */
  const addRow = () =>
    onChange(
      fields.map((f) => ({ ...f, values: [...f.values, { ...EMPTY_VALUE }] })),
      { immediate: true },
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* Media on the left, Content to the right; each group's label sits above
          its own columns and its "Add field" is pinned to that column's right.
          This area is the row scroller: the parent card pins full-height, so the
          rows scroll here (headers stay via the sticky <thead>) and the
          horizontal scrollbar sits at the bottom edge of the card. */}
      <div
        ref={boardScrollRef}
        className="flex min-h-0 flex-1 items-start gap-4 overflow-auto pb-1"
      >
        {(['media', 'content'] as TaxonomyFieldGroup[]).map((group) => {
          const gFields = group === 'media' ? mediaFields : contentFields;
          const meta = GROUP_META[group];
          const openAdd = () => {
            setAddAfterId(null);
            setAddParentId(null);
            setAddSkipPicker(false);
            setAddGroup(group);
          };

          if (gFields.length === 0) {
            return (
              <div key={group} className="flex shrink-0 flex-col gap-2">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className={`size-2 shrink-0 rounded-full ${meta.dot}`} aria-hidden />
                    <h3 className="whitespace-nowrap text-sm font-semibold text-foreground">
                      {meta.label}
                    </h3>
                    <Badge variant="ghost" roundness="full" className="border-border-light text-xs">
                      0
                    </Badge>
                  </div>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="mini"
                      className="h-7 shrink-0 whitespace-nowrap text-xs text-muted-foreground"
                      onClick={openAdd}
                    >
                      <Plus className="size-3.5" />
                      {t('taxonomy.fieldsEditor.addField')}
                    </Button>
                  )}
                </div>
                <div
                  className="flex items-center justify-center rounded-lg border border-dashed border-border-light px-6 py-8 text-center text-sm text-muted-foreground"
                  style={{ minWidth: MIN_GROUP_WIDTH }}
                >
                  {t('taxonomy.fieldsEditor.no')} {group} {t('taxonomy.fieldsEditor.fieldsYet')}
                </div>
              </div>
            );
          }

          return (
            <GroupTable
              key={group}
              group={group}
              fields={gFields}
              allFields={hydratedFields}
              linkedSchemaNames={linkedSchemaNames}
              rowCount={maxLen}
              rowWindow={rowWindow}
              onScrollRowIntoView={scrollRowIntoView}
              /*
               * Only the table holding the field acts on it. Each group owns its
               * own selection and scroller, so handing the target to both would
               * move two focus rings and fight over the scroll position.
               */
              focusTarget={
                focusTarget && gFields.some((f) => f.id === focusTarget.fieldId)
                  ? focusTarget
                  : null
              }
              readOnly={readOnly}
              hoveredRow={hoveredRow}
              onHoverRow={setHoveredRow}
              onSetCell={setCell}
              onSetCells={setCells}
              onAddField={openAdd}
              onEditField={setEditingField}
              onConfigureField={(f) =>
                f.type === 'linked' ? setLinkedFieldId(f.id) : setComposerFieldId(f.id)
              }
              onResetField={(f) => setResetFieldId(f.id)}
              onLinkList={(f) => setLinkListFieldId(f.id)}
              onUnlinkList={(f) => unlinkFromList(f.id)}
              resolvedLists={resolvedLists}
              cellProblems={cellProblems}
              fieldProblems={fieldProblems}
              onGenerateSample={handleGenerateSample}
              onAddFieldAfter={addSiblingAfter}
              onAddSubfield={addSubfield}
              onDetachField={detachField}
              onDuplicateField={duplicateField}
              onRemoveField={removeField}
              onResize={patchField}
              onReorder={reorderFields}
              onRename={(id, name) => patchField(id, { name })}
              onEditingChange={(key, open) => reportEditing(`${group}:${key}`, open)}
            />
          );
        })}
      </div>

      {hasAnyField && !readOnly && (
        <Button
          variant="outline"
          size="mini"
          className="h-8 w-fit justify-start text-xs text-muted-foreground"
          onClick={addRow}
        >
          <Plus className="size-3.5" />
          {t('taxonomy.fieldsEditor.addRow')}
        </Button>
      )}

      {composerFieldId &&
        (() => {
          const cf = fields.find((f) => f.id === composerFieldId);
          if (!cf) return null;
          return (
            <TrackingCodeComposer
              open
              field={cf}
              fields={fields}
              mintedCount={mintedCount}
              onClose={() => setComposerFieldId(null)}
              onSave={({ name, pattern }) => {
                patchField(cf.id, { name, pattern }, IMMEDIATE);
                setComposerFieldId(null);
              }}
            />
          );
        })()}

      {linkedFieldId &&
        (() => {
          const lf = fields.find((f) => f.id === linkedFieldId);
          if (!lf) return null;
          return (
            <LinkedSchemaComposer
              open
              field={lf}
              currentSchemaId={taxonomyId}
              onClose={() => setLinkedFieldId(null)}
              onSave={(link) => {
                patchField(lf.id, { link }, IMMEDIATE);
                setLinkedFieldId(null);
              }}
            />
          );
        })()}

      {linkListFieldId &&
        (() => {
          const lf = fields.find((f) => f.id === linkListFieldId);
          if (!lf) return null;
          return (
            <LinkListModal
              open
              field={lf}
              onClose={() => setLinkListFieldId(null)}
              onSave={(listRef) => patchField(lf.id, { listRef }, IMMEDIATE)}
            />
          );
        })()}

      {resetFieldId &&
        (() => {
          const rf = fields.find((f) => f.id === resetFieldId);
          const def = getStandardFieldByKey(rf?.libraryKey);
          if (!rf || !def) return null;
          return (
            <FormDialog
              open
              onOpenChange={(next) => !next && setResetFieldId(null)}
              title={t('taxonomy.fieldsEditor.resetToDefault', { name: rf.name })}
              className="flex flex-col sm:max-w-[28rem]"
            >
              <FormDialog.Body>
                <p className="text-sm text-muted-foreground">
                  <Trans
                    i18nKey="taxonomy.fieldsEditor.resetReplacesValues"
                    ns="knowledge"
                    count={def.values.length}
                    values={{ name: rf.name }}
                    components={{ strong: <strong /> }}
                  />
                </p>
              </FormDialog.Body>
              <FormDialog.Footer
                primary={{
                  label: t('taxonomy.fieldsEditor.resetDefault'),
                  onClick: () => resetFieldToDefault(rf.id),
                }}
                secondary={{
                  label: t('taxonomy.fieldsEditor.cancel'),
                  onClick: () => setResetFieldId(null),
                }}
              />
            </FormDialog>
          );
        })()}

      {addGroup && (
        <AddFieldModal
          open
          defaultGroup={addGroup}
          existingFields={fields}
          lockGroup={addParentId !== null}
          isSubfield={addParentId !== null}
          skipPicker={addSkipPicker}
          onClose={closeAddModal}
          onAdd={handleAdd}
        />
      )}
      {editingField && (
        <EditFieldModal
          open
          field={editingField}
          parentName={fields.find((f) => f.id === editingField.parentId)?.name}
          onClose={() => setEditingField(null)}
          onSave={(patch) => {
            patchField(editingField.id, patch, IMMEDIATE);
            setEditingField(null);
          }}
        />
      )}
    </div>
  );
}

FieldsEditor.displayName = 'FieldsEditor';

interface RowWindow {
  /** Absolute row indexes to render (the visible slice). */
  indexes: number[];
  /** Spacer heights (px) reserving the off-screen rows. */
  paddingTop: number;
  paddingBottom: number;
}

interface GroupTableProps {
  group: TaxonomyFieldGroup;
  fields: TaxonomyField[];
  /** Every field in the schema — used to resolve a Variable's pattern names. */
  allFields: TaxonomyField[];
  /** Schema id → name, for linked-field subtitles/cells. */
  linkedSchemaNames: Record<string, string>;
  rowCount: number;
  /** Windowed row slice shared across both group tables; null = render all rows. */
  rowWindow: RowWindow | null;
  /** Reveals a row the window never rendered. Only the parent owns the scroller. */
  onScrollRowIntoView: (row: number) => void;
  readOnly: boolean;
  hoveredRow: number | null;
  onHoverRow: (row: number | null) => void;
  onSetCell: (fieldId: string, row: number, patch: Partial<TaxonomyFieldValue>) => void;
  onSetCells: (
    writes: { fieldId: string; row: number; sub: 'value' | 'code'; value: string }[],
  ) => void;
  onAddField: () => void;
  onEditField: (field: TaxonomyField) => void;
  /** Open the Variable (computed) pattern composer. */
  onConfigureField: (field: TaxonomyField) => void;
  /** Reset a library field's values to the catalog defaults. */
  onResetField: (field: TaxonomyField) => void;
  /** Bind this column to a shared list / release it back to inline values. */
  onLinkList: (field: TaxonomyField) => void;
  onUnlinkList: (field: TaxonomyField) => void;
  /** Vocabulary resolved from the bound list, keyed by field id. */
  resolvedLists: Record<string, ResolvedList>;
  /** Validate findings per cell / per column — see {@link FieldsEditorProps}. */
  cellProblems?: Map<string, SchemaProblem[]>;
  fieldProblems?: Map<string, SchemaProblem[]>;
  /** A cell or column to reveal, already filtered to this group. */
  focusTarget?: FocusTarget | null;
  /** Mint a sample code for a Variable column. */
  onGenerateSample: (field: TaxonomyField) => void;
  onAddFieldAfter: (field: TaxonomyField) => void;
  onAddSubfield: (field: TaxonomyField) => void;
  onDetachField: (id: string) => void;
  onDuplicateField: (field: TaxonomyField) => void;
  onRemoveField: (id: string) => void;
  onResize: (fieldId: string, patch: Partial<TaxonomyField>) => void;
  onReorder: (activeId: string, overId: string) => void;
  onRename: (id: string, name: string) => void;
  /** `key` separates the cell editor from a column rename in the same table. */
  onEditingChange: (key: 'cell' | 'header', open: boolean) => void;
}

/** One bordered, accent-topped table for a single field group. */
function GroupTable(props: GroupTableProps) {
  const { t } = useTranslation('knowledge');
  const {
    group,
    fields,
    allFields,
    linkedSchemaNames,
    rowCount,
    rowWindow,
    onScrollRowIntoView,
    readOnly,
    hoveredRow,
    onHoverRow,
    focusTarget,
  } = props;
  const meta = GROUP_META[group];
  const rows = Array.from({ length: rowCount }, (_, i) => i);
  const [resizing, setResizing] = useState<{
    id: string;
    col: 'value' | 'code';
    width: number;
  } | null>(null);

  const colWidth = (field: TaxonomyField, col: 'value' | 'code') => {
    if (resizing && resizing.id === field.id && resizing.col === col) return resizing.width;
    const stored = col === 'value' ? field.valueWidth : field.codeWidth;
    return stored ?? (col === 'value' ? DEFAULT_VALUE_WIDTH : DEFAULT_CODE_WIDTH);
  };

  // Explicit table width = sum of the (resizable) column widths. table-fixed
  // makes those widths authoritative; an explicit px width (not 100%) keeps the
  // box hugging the table instead of grabbing the whole flex row.
  //
  // Floored at MIN_GROUP_WIDTH so the group header always has a single line to
  // render on. Any slack goes to the columns, which is the point — a lone field
  // in a 200px table is what squeezed the header in the first place.
  const tableWidth = Math.max(
    MIN_GROUP_WIDTH,
    fields.reduce((sum, f) => sum + colWidth(f, 'value') + colWidth(f, 'code'), 0),
  );

  const totalCols = fields.length * 2;

  const fieldIds = fields.map((f) => f.id);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    props.onReorder(String(active.id), String(over.id));
  };

  // ─── Spreadsheet selection + copy/paste (Google-Sheets-style) ─────────────
  // Cells are addressed by a flat sub-column index `c` (0..colCount-1): even = a
  // field's VALUE, odd = its CODE. A selection is an anchor→focus rectangle.
  const colCount = fields.length * 2;
  const gridRef = useRef<HTMLDivElement>(null);
  const [sel, setSel] = useState<Selection | null>(null);
  const [edit, setEditState] = useState<{ r: number; c: number } | null>(null);
  const reportCellEditor = useReportedEditorOpen((open) => props.onEditingChange('cell', open));
  const setEdit = (next: { r: number; c: number } | null) => {
    setEditState(next);
    reportCellEditor(next !== null);
  };
  const [editDraft, setEditDraft] = useState('');
  const draggingSel = useRef(false);

  useEffect(() => {
    const up = () => {
      draggingSel.current = false;
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const rect = sel
    ? {
        r0: Math.min(sel.ar, sel.fr),
        r1: Math.max(sel.ar, sel.fr),
        c0: Math.min(sel.ac, sel.fc),
        c1: Math.max(sel.ac, sel.fc),
      }
    : null;
  const inRect = (r: number, c: number) =>
    !!rect && r >= rect.r0 && r <= rect.r1 && c >= rect.c0 && c <= rect.c1;
  const fieldOfCol = (c: number) => fields[Math.floor(c / 2)];
  const subOfCol = (c: number): Sub => (c % 2 === 0 ? 'value' : 'code');
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
  const isDateCol = (c: number) => subOfCol(c) === 'value' && fieldOfCol(c)?.type === 'date';
  /** A column bound to a shared list is owned by that list — not editable here. */
  const isListBound = (f: TaxonomyField | undefined) => !!f?.listRef?.listId;
  // Variable (computed), Linked and list-bound columns are all derived — never writable.
  const isDerivedCol = (c: number) => {
    const f = fieldOfCol(c);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
    return !!f && (isGeneratedType(f.type) || isListBound(f));
  };
  /** Cell text: list-bound columns render the resolved list vocabulary. */
  const valAt = (r: number, c: number) => {
    const f = fieldOfCol(c);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
    if (!f) return '';
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
    if (isListBound(f)) return props.resolvedLists[f.id]?.values[r]?.[subOfCol(c)] ?? '';
    // A Variable draws ONE merged cell across both sub-columns, so its preview
    // belongs on the value half and the code half stays empty. Reading
    // `f.values` here returned two empties, so pasting a copied row into a
    // sheet silently dropped the generated code. Emitting the merged cell ONCE
    // is the copy loop's job, not this one's.
    if (f.type === 'computed')
      return subOfCol(c) === 'value' ? resolveVariableForRow(f, allFields, r) : '';
    // A linked column has no per-row value at all: what renders there is the
    // target schema's NAME, and the join itself resolves at asset-tagging time.
    if (f.type === 'linked') return '';
    return f.values[r]?.[subOfCol(c)] ?? '';
  };
  /**
   * Both cell-write funnels clamp, because only one of them passes through the
   * editor: a block paste is handled on the grid and never reaches an `<input>`,
   * so `maxLength` alone would leave the likelier route — a column pasted out of
   * a spreadsheet — writing values the API rejects.
   */
  const setAt = (r: number, c: number, v: string) => {
    const f = fieldOfCol(c);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
    if (f && !isGeneratedType(f.type) && !isListBound(f)) {
      const sub = subOfCol(c);
      props.onSetCell(f.id, r, { [sub]: clampFieldValue(sub, v) });
    }
  };
  /** Batched write of many cells at once (paste/fill/clear) — see onSetCells. */
  const setMany = (cells: { r: number; c: number; v: string }[]) => {
    const writes = cells
      .filter(({ c }) => {
        const f = fieldOfCol(c);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
        return f && !isGeneratedType(f.type) && !isListBound(f);
      })
      .map(({ r, c, v }) => {
        const sub = subOfCol(c);
        return { fieldId: fieldOfCol(c).id, row: r, sub, value: clampFieldValue(sub, v) };
      });
    props.onSetCells(writes);
  };

  const beginEdit = (r: number, c: number, initial?: string) => {
    // Date + generated (Variable / Linked) cells aren't edited via the text editor.
    if (readOnly || isDateCol(c) || isDerivedCol(c)) return;
    setEdit({ r, c });
    setEditDraft(initial ?? valAt(r, c));
  };
  /**
   * Escape drops the edit, but focusing the grid blurs the `<input>` first and
   * GridCell commits on blur — reaching `commitEdit` with an `edit`/`editDraft`
   * React has not re-rendered yet, so the discarded draft got written anyway.
   * A ref is read synchronously, which is the whole reason it is not state.
   */
  const discardingRef = useRef(false);
  const commitEdit = () => {
    if (discardingRef.current) {
      setEdit(null);
      return;
    }
    if (edit && !isDateCol(edit.c)) setAt(edit.r, edit.c, editDraft);
    setEdit(null);
  };

  /**
   * Commits when the virtualizer drops the row being edited.
   *
   * The `<input>` unmounts with the row while `edit` survives in state, so the
   * draft used to sit there until the next mousedown wrote it — into a row the
   * user had scrolled away from and could not see. Committing keeps what they
   * typed where they typed it; clearing would silently delete it.
   *
   * Still needed now that arrow keys scroll: a wheel scroll can drop the row
   * without touching the selection, and fast key-repeat can outrun the render
   * that updates `rowWindow`.
   */
  useEffect(() => {
    if (edit && rowWindow && !rowWindow.indexes.includes(edit.r)) commitEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- commitEdit closes over `editDraft`; re-running on every keystroke would commit mid-typing
  }, [rowWindow, edit]);

  const onCellMouseDown = (r: number, c: number, e: React.MouseEvent) => {
    pendingRevealRef.current = null;
    if (edit) commitEdit();
    e.preventDefault();
    // The grid is as tall as all its rows, so a plain focus scrolls it into view
    // mid-press, and the drag then selects every cell that slid under the pointer.
    gridRef.current?.focus({ preventScroll: true });
    if (e.shiftKey && sel) setSel({ ...sel, fr: r, fc: c });
    else setSel({ ar: r, ac: c, fr: r, fc: c });
    draggingSel.current = true;
  };
  const onCellMouseEnter = (r: number, c: number) => {
    if (draggingSel.current) setSel((s) => (s ? { ...s, fr: r, fc: c } : s));
  };

  const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

  /**
   * A cell `revealCell` could only reach through the virtualizer, revealed again
   * once its row mounts. The virtualizer scrolls only the board; the page around
   * it may still hide the row, and the cell's own `scrollIntoView` moves both.
   */
  const pendingRevealRef = useRef<{ r: number; c: number } | null>(null);
  useEffect(() => {
    const pending = pendingRevealRef.current;
    if (!pending) return;
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- as in revealCell
    const cell = gridRef.current?.querySelector<HTMLElement>(
      `[data-cell="${pending.r}-${pending.c}"]`,
    );
    if (!cell) return;
    pendingRevealRef.current = null;
    cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [rowWindow]);

  /**
   * Brings the cell the selection just moved to onto the screen.
   *
   * Prefers the DOM, which covers both axes at once and is the ONLY thing that
   * works below the virtualization threshold — the virtualizer is disabled
   * there, and a disabled virtualizer has no measurements, so `scrollToIndex`
   * silently does nothing. Falls back to the parent's virtualizer only when the
   * row was never rendered, which is the one case the DOM cannot reach.
   *
   * Without this, arrowing down a windowed grid walked the selection into rows
   * that were never mounted: no focus ring moved, the scroller stayed at 0, and
   * a subsequent keystroke edited a row the user could not see.
   */
  const revealCell = (r: number, c: number) => {
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- the rule cannot pin the non-deprecated `querySelector` overload for a generic element
    const cell = gridRef.current?.querySelector<HTMLElement>(`[data-cell="${r}-${c}"]`);
    if (cell) {
      pendingRevealRef.current = null;
      cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      return;
    }
    // Unwindowed, every row is rendered, so a miss has nothing to wait for.
    pendingRevealRef.current = rowWindow ? { r, c } : null;
    onScrollRowIntoView(r);
    /*
     * A group whose fields are ALL empty renders no row at all, and the
     * virtualizer is disabled below the threshold — so neither branch above can
     * move anything. That is the state a group is in while its fields are still
     * being drafted, which is exactly when `field_has_no_values` fires, so a
     * jump to that finding otherwise did nothing at all.
     *
     * The header is the only node that marks a column, and a column finding is
     * explained there anyway. It is sticky, so `block: 'nearest'` cannot move the
     * grid vertically; the horizontal scroll is the part that matters.
     */
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- as above
    const header = gridRef.current?.querySelector<HTMLElement>(`[data-col="${c}"]`);
    header?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  /**
   * Reveals the cell or column the details popover asked for.
   *
   * The same three steps a click already performs — reveal, select, return focus
   * to the grid — reused rather than reinvented, so a jumped-to cell behaves
   * exactly like a clicked one and the arrow keys carry on from there.
   *
   * A column finding has no row, so it lands on row 0 of that column: the header
   * carries the explanation, and scrolling the column into view is the part the
   * user cannot do for themselves.
   */
  useEffect(() => {
    if (!focusTarget) return;
    const index = fields.findIndex((f) => f.id === focusTarget.fieldId);
    if (index < 0) return;

    const column = index * 2 + (focusTarget.column === 'code' ? 1 : 0);
    const row = focusTarget.row ?? 0;

    // Past the window the row is not mounted, so `revealCell` falls back to the
    // virtualizer — which is the whole reason this exists.
    revealCell(row, column);
    setSel({ ar: row, ac: column, fr: row, fc: column });
    gridRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `nonce` is what makes a repeat click repeat; the rest are stable for a given target
  }, [focusTarget?.nonce]);

  const onGridKeyDown = (e: React.KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (edit || !sel || !rect || readOnly) return;
    if (e.ctrlKey || e.metaKey) return; // copy/cut/paste handled by native events
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      clearRect();
      return;
    }
    if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault();
      beginEdit(sel.fr, sel.fc);
      return;
    }
    const moves: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    const m = moves[e.key];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
    if (m) {
      e.preventDefault();
      const nr = clamp(sel.fr + m[0], 0, Math.max(0, rowCount - 1));
      const nc = clamp(sel.fc + m[1], 0, colCount - 1);
      if (e.shiftKey) setSel({ ...sel, fr: nr, fc: nc });
      else setSel({ ar: nr, ac: nc, fr: nr, fc: nc });
      revealCell(nr, nc);
      return;
    }
    if (e.key.length === 1 && !e.altKey) {
      // The editor mounts and takes focus before the browser inserts the key, so an unprevented
      // keydown types it a second time into a draft already seeded with it.
      e.preventDefault();
      beginEdit(sel.fr, sel.fc, e.key);
    }
  };

  const onGridCopy = (e: React.ClipboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (edit || !rect) return;
    const lines: string[] = [];
    for (let r = rect.r0; r <= rect.r1; r++) {
      const cols: string[] = [];
      for (let c = rect.c0; c <= rect.c1; c++) {
        const f = fieldOfCol(c);
        // A generated column draws ONE cell across both sub-columns, so it
        // contributes one value — otherwise a copied row carried a phantom
        // empty per merged column and pasting it landed a column out of step.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] by a coordinate that can fall outside
        if (f && isGeneratedType(f.type)) {
          const vc = Math.floor(c / 2) * 2;
          // Emit at the value half, or at the code half when that is all the
          // rect covers — either way exactly once per merged cell.
          if (c !== Math.max(vc, rect.c0)) continue;
          cols.push(valAt(r, vc));
          continue;
        }
        cols.push(valAt(r, c));
      }
      lines.push(cols.join('\t'));
    }
    e.clipboardData.setData('text/plain', lines.join('\n'));
    e.preventDefault();
  };

  const clearRect = () => {
    if (!rect) return;
    const cells: { r: number; c: number; v: string }[] = [];
    for (let r = rect.r0; r <= rect.r1; r++)
      for (let c = rect.c0; c <= rect.c1; c++) cells.push({ r, c, v: '' });
    setMany(cells);
  };

  const onGridCut = (e: React.ClipboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (edit || !rect) return;
    onGridCopy(e);
    clearRect();
  };

  const onGridPaste = (e: React.ClipboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (edit || !rect || readOnly) return;
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    e.preventDefault();
    const grid = text
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .map((l) => l.split('\t'));
    if (grid.length > 1 && grid[grid.length - 1].length === 1 && grid[grid.length - 1][0] === '')
      grid.pop();
    if (grid.length === 0) return;
    const cells: { r: number; c: number; v: string }[] = [];
    // A single copied value fills the whole current selection (Sheets behavior).
    if (grid.length === 1 && grid[0].length === 1 && (rect.r1 > rect.r0 || rect.c1 > rect.c0)) {
      const v = grid[0][0];
      for (let r = rect.r0; r <= rect.r1; r++)
        for (let c = rect.c0; c <= rect.c1; c++) cells.push({ r, c, v });
    } else {
      // Otherwise spill the block from the selection's top-left.
      grid.forEach((line, dr) =>
        line.forEach((v, dc) => {
          const c = rect.c0 + dc;
          if (c < colCount) cells.push({ r: rect.r0 + dr, c, v });
        }),
      );
    }
    setMany(cells);
  };

  const onEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
      gridRef.current?.focus({ preventScroll: true });
      if (edit) {
        // Sheets behaviour: committing moves down, so a column can be typed
        // without reaching for the mouse. Clamped exactly like the arrow keys.
        const nr = clamp(edit.r + 1, 0, Math.max(0, rowCount - 1));
        setSel({ ar: nr, ac: edit.c, fr: nr, fc: edit.c });
        revealCell(nr, edit.c);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      discardingRef.current = true;
      setEdit(null);
      // Fires the blur commit synchronously, which the flag above swallows.
      gridRef.current?.focus({ preventScroll: true });
      discardingRef.current = false;
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- scroll container that owns the spreadsheet key handling; the tables inside carry the semantics
    <div
      ref={gridRef}
      tabIndex={readOnly ? undefined : 0}
      onKeyDown={onGridKeyDown}
      onCopy={onGridCopy}
      onCut={onGridCut}
      onPaste={onGridPaste}
      // The cells draw the grid's lines, so the rounded clip cuts them at the bottom
      // corners; the overlay draws the outline, curve included, on top of them. It sits
      // under the sticky header, which keeps its own rounded top while scrolled.
      className="relative shrink-0 overflow-clip rounded-lg outline-none after:pointer-events-none after:absolute after:inset-0 after:z-20 after:rounded-lg after:border after:border-border-light"
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
      >
        <table
          className="table-fixed border-separate border-spacing-0 text-sm"
          style={{ width: tableWidth }}
        >
          <colgroup>
            {fields.map((field) => (
              <Fragment key={field.id}>
                <col style={{ width: colWidth(field, 'value') }} />
                <col style={{ width: colWidth(field, 'code') }} />
              </Fragment>
            ))}
          </colgroup>
          <thead className="sticky top-0 z-30">
            {/* Group label + accent — part of the sticky header so it (and the
              colored bar) stay pinned with the column headers while scrolling. */}
            <tr>
              <th
                colSpan={totalCols}
                // The square backing fills the corners outside this cell's curve, so lines
                // scrolled under the pinned header do not show there.
                className="relative rounded-t-lg border-x border-t border-border-light bg-card px-3 py-2 text-left before:absolute before:-inset-px before:-z-10 before:bg-card"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className={`size-2 shrink-0 rounded-full ${meta.dot}`} aria-hidden />
                    <h3 className="whitespace-nowrap text-sm font-semibold text-foreground">
                      {meta.label}
                    </h3>
                    <Badge variant="ghost" roundness="full" className="border-border-light text-xs">
                      {fields.length}
                    </Badge>
                  </div>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="mini"
                      className="h-7 shrink-0 whitespace-nowrap text-xs text-muted-foreground"
                      onClick={props.onAddField}
                    >
                      <Plus className="size-3.5" />
                      {t('taxonomy.fieldsEditor.addField')}
                    </Button>
                  )}
                </div>
              </th>
            </tr>
            <tr>
              <SortableContext items={fieldIds} strategy={horizontalListSortingStrategy}>
                {fields.map((field, i) => (
                  <SortableFieldHeaderCell
                    key={field.id}
                    field={field}
                    allFields={allFields}
                    targetName={
                      field.link?.targetSchemaId
                        ? linkedSchemaNames[field.link.targetSchemaId]
                        : undefined
                    }
                    isLast={i === fields.length - 1}
                    accent={meta.accent}
                    readOnly={readOnly}
                    onRename={(name) => props.onRename(field.id, name)}
                    onRenameEditingChange={(open) => props.onEditingChange('header', open)}
                    onEdit={() => props.onEditField(field)}
                    onConfigure={() => props.onConfigureField(field)}
                    onReset={() => props.onResetField(field)}
                    onLinkList={() => props.onLinkList(field)}
                    onUnlinkList={() => props.onUnlinkList(field)}
                    boundList={props.resolvedLists[field.id]}
                    problems={props.fieldProblems?.get(field.id)}
                    onGenerateSample={() => props.onGenerateSample(field)}
                    onAddRight={() => props.onAddFieldAfter(field)}
                    onAddSubfield={() => props.onAddSubfield(field)}
                    onDetach={() => props.onDetachField(field.id)}
                    onDuplicate={() => props.onDuplicateField(field)}
                    onRemove={() => props.onRemoveField(field.id)}
                  />
                ))}
              </SortableContext>
            </tr>
            <tr>
              {fields.map((field, i) => (
                <Fragment key={field.id}>
                  <SubHeaderCell
                    label={t('taxonomy.fieldsEditor.value')}
                    readOnly={readOnly}
                    col={i * 2}
                    width={colWidth(field, 'value')}
                    onLive={(w) => setResizing({ id: field.id, col: 'value', width: w })}
                    onCommit={(w) => {
                      setResizing(null);
                      props.onResize(field.id, { valueWidth: w });
                    }}
                  />
                  <SubHeaderCell
                    label={t('taxonomy.fieldsEditor.code')}
                    readOnly={readOnly}
                    col={i * 2 + 1}
                    width={colWidth(field, 'code')}
                    isLast={i === fields.length - 1}
                    onLive={(w) => setResizing({ id: field.id, col: 'code', width: w })}
                    onCommit={(w) => {
                      setResizing(null);
                      props.onResize(field.id, { codeWidth: w });
                    }}
                  />
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Windowing: a tall spacer <tr> reserves the off-screen rows above
              the visible slice (mirrors the Trackers board). */}
            {rowWindow && rowWindow.paddingTop > 0 && (
              <tr aria-hidden>
                <td colSpan={totalCols} style={{ height: rowWindow.paddingTop, padding: 0 }} />
              </tr>
            )}
            {(rowWindow ? rowWindow.indexes : rows).map((row) => (
              <tr
                key={row}
                className={hoveredRow === row ? 'bg-muted/40' : undefined}
                onMouseEnter={() => onHoverRow(row)}
                onMouseLeave={() => onHoverRow(null)}
              >
                {fields.map((field, fi) => {
                  const vc = fi * 2;
                  const cc = fi * 2 + 1;
                  // Generated columns (Variable / Linked): auto-resolved, no editable
                  // cells — one muted, non-interactive cell spanning both sub-columns.
                  if (isGeneratedType(field.type)) {
                    const isLastField = fi === fields.length - 1;
                    // Variable resolves a per-row preview; a linked join key resolves at
                    // asset-tagging time, so show a placeholder in the builder.
                    const generated =
                      field.type === 'computed' ? resolveVariableForRow(field, allFields, row) : '';
                    const linked = field.type === 'linked';
                    const targetName = field.link?.targetSchemaId
                      ? linkedSchemaNames[field.link.targetSchemaId]
                      : undefined;
                    // One cell spans BOTH sub-columns, so it belongs to the
                    // selection whenever either half is in the rect. Without
                    // this the column stayed untinted and unclickable while its
                    // neighbours highlighted around it — and it is copyable, so
                    // looking unselectable was the misleading part.
                    const genSelected = inRect(row, vc) || inRect(row, cc);
                    const genFocus = !!sel && sel.fr === row && (sel.fc === vc || sel.fc === cc);
                    return (
                      <td
                        key={field.id}
                        colSpan={2}
                        data-cell={`${row}-${vc}`}
                        className={`select-none border-b border-l border-border-light ${
                          genSelected ? 'bg-primary/10' : 'bg-surface/30'
                        } ${genFocus ? 'z-10 ring-2 ring-inset ring-primary' : ''} ${
                          isLastField ? 'border-r' : ''
                        }`}
                        // Mirrors GridCell: `readOnly` strips the handlers, and
                        // nothing here is editable anyway — selection and copy
                        // are the whole interaction.
                        {...(readOnly
                          ? {}
                          : {
                              onMouseDown: (e: React.MouseEvent) => onCellMouseDown(row, vc, e),
                              onMouseEnter: () => onCellMouseEnter(row, cc),
                            })}
                      >
                        <div
                          className="flex h-9 items-center gap-1 truncate px-2 font-mono text-xs text-muted-foreground"
                          title={linked ? undefined : generated || undefined}
                        >
                          {linked ? (
                            field.link?.targetSchemaId ? (
                              <>
                                <ArrowUpRight className="size-3 shrink-0" aria-hidden />
                                <span className="truncate">
                                  {targetName ?? t('taxonomy.fieldsEditor.linked')}
                                </span>
                              </>
                            ) : (
                              <span className="text-muted-foreground/40">
                                {t('taxonomy.fieldsEditor.notConfigured')}
                              </span>
                            )
                          ) : (
                            generated || <span className="text-muted-foreground/40">—</span>
                          )}
                        </div>
                      </td>
                    );
                  }
                  // A list-bound column is owned by the shared list: still
                  // selectable/copyable, but not editable from the schema — which
                  // is enforced by `beginEdit`/`setAt`/`setMany`, NOT by
                  // `readOnly`, whose extra effect is to strip mouse handlers.
                  // Those three cover the edit/commit flow only, so the date
                  // picker — which writes directly — is gated by `isBound`.
                  // The live pattern check only looks at the VALUE column: the
                  // backend enforces the pattern against the value a caller
                  // supplies, so flagging the code column would warn about
                  // something generation never tests. Validate findings for
                  // either column are merged in on top.
                  const liveViolation = fieldPatternViolation(
                    valAt(row, vc),
                    field.validationPattern,
                  );
                  const valueProblems = props.cellProblems?.get(cellKey(field.id, row, 'value'));
                  const codeProblems = props.cellProblems?.get(cellKey(field.id, row, 'code'));
                  return (
                    <Fragment key={field.id}>
                      <GridCell
                        value={valAt(row, vc)}
                        isDate={field.type === 'date'}
                        mask={field.format}
                        violation={mergeViolation(liveViolation, valueProblems)}
                        violationSeverity={violationSeverityOf(liveViolation, valueProblems)}
                        selected={inRect(row, vc)}
                        isFocus={!!sel && sel.fr === row && sel.fc === vc}
                        editing={!!edit && edit.r === row && edit.c === vc}
                        dataCell={`${row}-${vc}`}
                        maxLength={MAX_CELL_VALUE_LENGTH}
                        // NOT `|| boundCol`: `readOnly` also strips every mouse
                        // handler, which took away the selecting and copying the
                        // comment above promises. Editing is already refused by
                        // `beginEdit` via `isDerivedCol`, and by `setAt`/`setMany`.
                        readOnly={readOnly}
                        // The one write path those three guards do NOT cover is
                        // the date picker, which writes through `onDateChange`
                        // rather than the edit/commit flow.
                        isBound={isListBound(field)}
                        lastCol={vc === colCount - 1}
                        editDraft={editDraft}
                        onMouseDown={(e) => onCellMouseDown(row, vc, e)}
                        onMouseEnter={() => onCellMouseEnter(row, vc)}
                        onDoubleClick={() => beginEdit(row, vc)}
                        onEditChange={setEditDraft}
                        onEditKeyDown={onEditKeyDown}
                        onCommit={commitEdit}
                        onDateChange={(v) => setAt(row, vc, v)}
                      />
                      <GridCell
                        value={valAt(row, cc)}
                        muted
                        violation={describeProblems(codeProblems)}
                        violationSeverity={violationSeverityOf(null, codeProblems)}
                        selected={inRect(row, cc)}
                        isFocus={!!sel && sel.fr === row && sel.fc === cc}
                        editing={!!edit && edit.r === row && edit.c === cc}
                        dataCell={`${row}-${cc}`}
                        maxLength={MAX_CELL_CODE_LENGTH}
                        // See the value cell above — editing is guarded elsewhere.
                        readOnly={readOnly}
                        // The code half is owned by the list too, and the user
                        // double-clicks it just as often as the value half.
                        isBound={isListBound(field)}
                        lastCol={cc === colCount - 1}
                        editDraft={editDraft}
                        onMouseDown={(e) => onCellMouseDown(row, cc, e)}
                        onMouseEnter={() => onCellMouseEnter(row, cc)}
                        onDoubleClick={() => beginEdit(row, cc)}
                        onEditChange={setEditDraft}
                        onEditKeyDown={onEditKeyDown}
                        onCommit={commitEdit}
                        onDateChange={(v) => setAt(row, cc, v)}
                      />
                    </Fragment>
                  );
                })}
                {/* No per-row ✕ any more: rows blank across every editable
                    column are pruned automatically on commit. The button it
                    replaced deleted from a bound column's hidden inline array
                    and looked like it always removed the last row. */}
              </tr>
            ))}
            {rowWindow && rowWindow.paddingBottom > 0 && (
              <tr aria-hidden>
                <td colSpan={totalCols} style={{ height: rowWindow.paddingBottom, padding: 0 }} />
              </tr>
            )}
          </tbody>
        </table>
      </DndContext>
    </div>
  );
}

/** A resizable VALUE/CODE sub-column header with a drag handle on its right edge. */
function SubHeaderCell({
  label,
  width,
  readOnly,
  isLast,
  onLive,
  onCommit,
  col,
}: {
  label: string;
  width: number;
  readOnly: boolean;
  isLast?: boolean;
  onLive: (w: number) => void;
  onCommit: (w: number) => void;
  col: number;
}) {
  return (
    <th
      data-col={col}
      className={`relative select-none border-b border-l border-border-light bg-muted px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground ${
        isLast ? 'border-r' : ''
      }`}
    >
      {label}
      {!readOnly && <ColumnResizer width={width} onLive={onLive} onCommit={onCommit} />}
    </th>
  );
}

/** Drag handle that reports a live width during drag and commits on release. */
function ColumnResizer({
  width,
  onLive,
  onCommit,
}: {
  width: number;
  onLive: (w: number) => void;
  onCommit: (w: number) => void;
}) {
  const { t } = useTranslation('knowledge');
  /**
   * Pointer CAPTURE on the handle, with the listeners on the handle itself.
   *
   * They used to go on `window`, removed only by the `pointerup` handler — so
   * unmounting mid-drag (switching version, closing the tab) left them attached,
   * and the next stray `pointerup` anywhere on the page committed a width
   * through a stale closure over a column that was gone.
   *
   * Capture routes every subsequent pointer event to this element even when the
   * pointer leaves it, which is what a drag needs, and the browser releases it
   * automatically when the element is removed — so there is nothing left to leak.
   */
  const handlePointerDown = (e: React.PointerEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const handle = e.currentTarget;
    const startX = e.clientX;
    const startWidth = width;
    const clamp = (clientX: number) => Math.max(MIN_COL_WIDTH, startWidth + (clientX - startX));
    const move = (ev: PointerEvent) => onLive(clamp(ev.clientX));
    const up = (ev: PointerEvent) => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      onCommit(clamp(ev.clientX));
    };
    handle.setPointerCapture(e.pointerId);
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    // Capture can be lost without a pointerup (a browser gesture takes over),
    // which would otherwise leave the drag armed forever.
    handle.addEventListener('pointercancel', up);
  };

  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={t('taxonomy.fieldsEditor.resizeColumn')}
      onPointerDown={handlePointerDown}
      className="absolute -right-1.5 top-0 z-20 h-full w-3 cursor-col-resize touch-none select-none bg-transparent hover:bg-primary/40 active:bg-primary/60"
    />
  );
}

interface SortableFieldHeaderCellProps {
  field: TaxonomyField;
  allFields: TaxonomyField[];
  /** For a linked field: the target schema's display name. */
  targetName?: string;
  isLast: boolean;
  accent: string;
  readOnly: boolean;
  onRename: (name: string) => void;
  onRenameEditingChange: (open: boolean) => void;
  onEdit: () => void;
  onConfigure: () => void;
  onReset: () => void;
  onLinkList: () => void;
  onUnlinkList: () => void;
  /** Set when this column is bound to a shared list. */
  boundList?: ResolvedList;
  /** Validate findings anchored to this whole column. */
  problems?: SchemaProblem[];
  onGenerateSample: () => void;
  onAddRight: () => void;
  onAddSubfield: () => void;
  onDetach: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

/** Column header cell made drag-sortable (dnd-kit). Dragging reorders columns;
 *  a top-level drag carries its subfield cluster (see FieldsEditor.reorderFields). */
function SortableFieldHeaderCell({
  field,
  allFields,
  targetName,
  isLast,
  accent,
  readOnly,
  onRename,
  onRenameEditingChange,
  onEdit,
  onConfigure,
  onReset,
  onLinkList,
  onUnlinkList,
  boundList,
  problems,
  onGenerateSample,
  onAddRight,
  onAddSubfield,
  onDetach,
  onDuplicate,
  onRemove,
}: SortableFieldHeaderCellProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    disabled: readOnly,
  });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { zIndex: 30, position: 'relative' } : {}),
  };
  return (
    <th
      ref={setNodeRef}
      colSpan={2}
      scope="colgroup"
      style={style}
      className={`group/field overflow-hidden border-b border-l border-border-light px-2 py-2 text-left align-top ${
        field.parentId
          ? `border-t-2 border-dashed ${accent} bg-[color-mix(in_srgb,var(--surface)_50%,var(--card))]`
          : `border-t-2 ${accent} bg-surface`
      } ${isLast ? 'border-r' : ''} ${isDragging ? 'shadow-md' : ''}`}
    >
      <FieldHeaderCell
        field={field}
        allFields={allFields}
        targetName={targetName}
        readOnly={readOnly}
        isSubfield={!!field.parentId}
        dragHandleProps={readOnly ? undefined : { ...attributes, ...listeners }}
        onRename={onRename}
        onRenameEditingChange={onRenameEditingChange}
        onEdit={onEdit}
        onConfigure={onConfigure}
        onReset={onReset}
        onLinkList={onLinkList}
        onUnlinkList={onUnlinkList}
        boundList={boundList}
        problems={problems}
        onGenerateSample={onGenerateSample}
        onAddRight={onAddRight}
        onAddSubfield={onAddSubfield}
        onDetach={onDetach}
        onDuplicate={onDuplicate}
        onRemove={onRemove}
      />
    </th>
  );
}

interface FieldHeaderCellProps {
  field: TaxonomyField;
  allFields: TaxonomyField[];
  /** For a linked field: the target schema's display name. */
  targetName?: string;
  readOnly: boolean;
  isSubfield: boolean;
  dragHandleProps?: Record<string, unknown>;
  onRename: (name: string) => void;
  onRenameEditingChange: (open: boolean) => void;
  onEdit: () => void;
  onConfigure: () => void;
  onReset: () => void;
  onLinkList: () => void;
  onUnlinkList: () => void;
  /** Set when this column is bound to a shared list. */
  boundList?: ResolvedList;
  /** Validate findings anchored to this whole column. */
  problems?: SchemaProblem[];
  onGenerateSample: () => void;
  onAddRight: () => void;
  onAddSubfield: () => void;
  onDetach: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

/**
 * Column header: field name (required shown with a trailing asterisk) + a quiet
 * type subtitle. On hover a ⋮ menu (Trackers pattern) exposes Edit / Add field
 * to the right / Duplicate / Delete; Edit opens the field modal to change
 * title, type and required — keeping the header itself low-noise.
 */
function FieldHeaderCell({
  field,
  allFields,
  targetName,
  readOnly,
  isSubfield,
  dragHandleProps,
  onRename,
  onRenameEditingChange,
  onEdit,
  onConfigure,
  onReset,
  onLinkList,
  onUnlinkList,
  boundList,
  problems,
  onGenerateSample,
  onAddRight,
  onAddSubfield,
  onDetach,
  onDuplicate,
  onRemove,
}: FieldHeaderCellProps) {
  const { t } = useTranslation('knowledge');
  const [editing, setEditingState] = useState(false);
  const reportRename = useReportedEditorOpen(onRenameEditingChange);
  const setEditing = (open: boolean) => {
    setEditingState(open);
    reportRename(open);
  };
  const [draft, setDraft] = useState(field.name);
  const problemSummary = describeProblems(problems);
  const problemSeverity = violationSeverityOf(null, problems);
  const isComputed = field.type === 'computed';
  const isLinked = field.type === 'linked';
  const linkedSchemaFieldsOn = useBooleanFlagValue('taxonomy_linked_schema_fields', false);
  // `all` means "every level", which is the default and not worth a badge. Null
  // for an unrecognised stored value, which the badge then omits rather than
  // rendering the word "null" as it used to.
  const levelLabel = field.level && field.level !== 'all' ? taxonomyLevelLabel(field.level) : null;
  const isGenerated = isComputed || isLinked;
  // Library-sourced fields can be reset to their catalog defaults.
  const canReset = !isGenerated && !!getStandardFieldByKey(field.libraryKey);
  // Generated columns show what they resolve to: a Variable its pattern, a
  // linked field its target + source (or a prompt to configure).
  const subtitle = isComputed
    ? field.pattern?.length
      ? patternToString(field.pattern, allFields)
      : t('taxonomy.fieldsEditor.variableNotConfigured')
    : isLinked
      ? describeLink(field.link, targetName)
      : boundList
        ? // WHICH list drives this column, WHICH version, and whether that
          // version can still change. A following column showed the bare name,
          // so the two states differed only by the presence of `· v3` — which
          // reads as decoration rather than as "pinned", and told nobody which
          // vocabulary the generator would actually use.
          // No version yet means the list is still loading or could not be
          // resolved at all — `listName` already says which. Appending
          // "following v?" there would put a version claim on every cold load of
          // every bound column, which is the common path.
          boundList.versionNumber === null
          ? boundList.listName
          : boundList.follows
            ? i18n.t('knowledge:taxonomy.fieldsEditor.listFollowingVersion', {
                name: boundList.listName,
                version: boundList.versionNumber,
              })
            : i18n.t('knowledge:taxonomy.fieldsEditor.listPinnedToVersion', {
                name: boundList.listName,
                version: boundList.versionNumber,
              })
        : isSubfield
          ? i18n.t('knowledge:taxonomy.fieldsEditor.subfieldOfType', {
              type: TAXONOMY_FIELD_TYPE_LABELS[field.type],
            })
          : TAXONOMY_FIELD_TYPE_LABELS[field.type];
  const emitsDisplayAsCode =
    !isGenerated && !!boundList && boundList.versionNumber !== null && !field.listRef?.codeColumnId;
  const headerSubtitle = emitsDisplayAsCode
    ? i18n.t('knowledge:taxonomy.fieldsEditor.withCodesSameAsDisplay', { label: subtitle })
    : subtitle;

  const startEdit = () => {
    if (readOnly) return;
    setDraft(field.name);
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== field.name) onRename(next);
  };

  return (
    <div className="flex items-start justify-between gap-1">
      <div className="flex min-w-0 items-start gap-1">
        {dragHandleProps && (
          <button
            type="button"
            aria-label={t('taxonomy.fieldsEditor.dragReorderColumn')}
            {...(dragHandleProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
            className="mt-0.5 shrink-0 cursor-grab touch-none text-muted-foreground opacity-0 transition-opacity group-hover/field:opacity-100 active:cursor-grabbing"
          >
            <GripVertical className="size-3.5" />
          </button>
        )}
        {isSubfield && (
          <CornerDownRight
            className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
            aria-label={t('taxonomy.fieldsEditor.subfield')}
          />
        )}
        <div className="flex min-w-0 flex-col">
          {editing ? (
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the input only mounts once the user starts editing, so focusing it is the interaction
              autoFocus
              value={draft}
              maxLength={100}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                } else if (e.key === 'Escape') {
                  setDraft(field.name);
                  setEditing(false);
                }
              }}
              className="w-full rounded border border-primary bg-background px-1 py-0.5 text-sm font-medium text-foreground outline-none"
            />
          ) : (
            <span className="flex min-w-0 items-center gap-1">
              {/* Read-only degrades to text rather than a dead button: a column
                  name that looks pressable and never is reads as broken. */}
              {readOnly ? (
                <span className="truncate text-left text-sm font-medium text-foreground">
                  {field.name}
                  {field.required && <span className="ml-0.5 text-destructive">*</span>}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={startEdit}
                  title={t('taxonomy.fieldsEditor.clickRename')}
                  className="truncate text-left text-sm font-medium text-foreground decoration-dotted underline-offset-2 hover:underline"
                >
                  {field.name}
                  {field.required && <span className="ml-0.5 text-destructive">*</span>}
                </button>
              )}
              {/* Column-level Validate findings — a broken binding, a rule that
                  can never fire, a pattern nothing enforces. Same triangle the
                  cells use, so one icon means one thing across the grid. */}
              {problemSummary && (
                <ProblemTriangle message={problemSummary} warning={problemSeverity === 'warning'} />
              )}
            </span>
          )}
          <span className="flex min-w-0 items-center gap-1">
            {boundList && (
              <ListTree
                className={`size-3 shrink-0 ${boundList.error ? 'text-destructive' : 'text-muted-foreground'}`}
                aria-label={boundList.error ?? t('taxonomy.fieldsEditor.boundSharedList')}
              />
            )}
            {levelLabel && (
              <span
                className="shrink-0 rounded bg-accent px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                title={t('taxonomy.fieldsEditor.hierarchyLevel', { level: levelLabel })}
              >
                {levelLabel}
              </span>
            )}
            <span
              className={`truncate text-xs font-normal text-muted-foreground ${isGenerated ? 'font-mono' : ''}`}
              title={isGenerated ? subtitle : undefined}
            >
              {headerSubtitle}
            </span>
          </span>
        </div>
      </div>
      {!readOnly && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="mini"
              aria-label={t('taxonomy.fieldsEditor.fieldActions', { name: field.name })}
              className="size-6 shrink-0 p-0 text-muted-foreground opacity-0 transition-opacity group-hover/field:opacity-100 data-[state=open]:opacity-100"
            >
              <MoreVertical className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {isGenerated ? (
              <>
                <DropdownMenuItem onSelect={onConfigure}>
                  <Settings2 className="mr-2 size-4" />
                  {t('taxonomy.fieldsEditor.configure')}
                </DropdownMenuItem>
                {isComputed && (
                  <DropdownMenuItem onSelect={onGenerateSample} disabled={!field.pattern?.length}>
                    <Play className="mr-2 size-4" />
                    {t('taxonomy.fieldsEditor.generateSample')}
                  </DropdownMenuItem>
                )}
              </>
            ) : (
              <DropdownMenuItem onSelect={onEdit}>
                <Pencil className="mr-2 size-4" />
                {t('taxonomy.fieldsEditor.edit')}
              </DropdownMenuItem>
            )}
            {/* Shared-list binding — only meaningful for option-bearing columns,
                so LINKING is offered on those alone.

                UNLINKING is offered wherever a binding exists, including on a
                generated column: every gate here is a gate on the only way out
                of a binding that should never have been written, and a stored
                row can carry one whatever its type says. */}
            {boundList ? (
              <>
                {!isGenerated && canBindList(field.type) && (
                  <DropdownMenuItem onSelect={onLinkList}>
                    <ListTree className="mr-2 size-4" />
                    {t('taxonomy.fieldsEditor.editListBinding')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={onUnlinkList}>
                  <Unlink className="mr-2 size-4" />
                  {t('taxonomy.fieldsEditor.unlinkList')}
                </DropdownMenuItem>
              </>
            ) : (
              !isGenerated &&
              canBindList(field.type) && (
                <DropdownMenuItem onSelect={onLinkList}>
                  <ListTree className="mr-2 size-4" />
                  {t('taxonomy.fieldsEditor.useSharedList')}
                </DropdownMenuItem>
              )
            )}
            {canReset && !boundList && (
              <DropdownMenuItem onSelect={onReset}>
                <RotateCcw className="mr-2 size-4" />
                {t('taxonomy.fieldsEditor.resetDefault')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={onAddRight}>
              <ArrowRightToLine className="mr-2 size-4" />
              {t('taxonomy.fieldsEditor.addFieldRight')}
            </DropdownMenuItem>
            {!isSubfield && !isGenerated && (
              <DropdownMenuItem onSelect={onAddSubfield}>
                <CornerDownRight className="mr-2 size-4" />
                {t('taxonomy.fieldsEditor.addSubfield')}
              </DropdownMenuItem>
            )}
            {/* The way back out of a cluster. Without it a subfield was stuck:
                its section is locked to the parent, so the only escape was
                deleting and recreating it. */}
            {isSubfield && (
              <DropdownMenuItem onSelect={onDetach}>
                <CornerUpLeft className="mr-2 size-4" />
                {t('taxonomy.fieldsEditor.detachParent')}
              </DropdownMenuItem>
            )}
            {(!isLinked || linkedSchemaFieldsOn) && (
              <DropdownMenuItem onSelect={onDuplicate}>
                <Copy className="mr-2 size-4" />
                {t('taxonomy.fieldsEditor.duplicate')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onSelect={onRemove}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 size-4" />
              {t('taxonomy.fieldsEditor.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

interface GridCellProps {
  value: string;
  muted?: boolean;
  isDate?: boolean;
  mask?: string | null;
  /** Why this value breaks the field's validation pattern, if it does. */
  violation?: string | null;
  /** How loudly to paint it. A warning must not look like an error. */
  violationSeverity?: 'error' | 'warning' | null;
  selected: boolean;
  isFocus: boolean;
  editing: boolean;
  readOnly: boolean;
  /**
   * The column draws its values from a shared list, so nothing here may write.
   *
   * Separate from `readOnly` on purpose: `readOnly` also strips the mouse
   * handlers, and a bound column still has to be selectable and copyable.
   */
  isBound?: boolean;
  /**
   * `row-col`, so keyboard navigation can find the cell in the DOM and scroll
   * it into view. The coordinate has to survive to the rendered element —
   * nothing else on a `<td>` identifies which cell it is.
   */
  dataCell: string;
  lastCol: boolean;
  editDraft: string;
  /** Matches the API's cap for this sub-column, so typing stops where the save would. */
  maxLength: number;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onDoubleClick: () => void;
  onEditChange: (v: string) => void;
  onEditKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onCommit: () => void;
  onDateChange: (v: string) => void;
}

/**
 * One spreadsheet cell. Cheap by default (a text `<div>`) — it only mounts an
 * `<input>` while being edited, so a full grid stays light. Selection is drawn
 * with a tinted background (in-range) + a ring on the focus cell. Date VALUE
 * cells render selectable text plus a calendar-icon `DatePickerButton` — unless
 * the column is list-bound, where there is nothing here that may be written.
 */
function GridCell({
  value,
  muted,
  isDate,
  mask,
  violation,
  violationSeverity,
  selected,
  isFocus,
  editing,
  readOnly,
  isBound,
  dataCell,
  lastCol,
  editDraft,
  maxLength,
  onMouseDown,
  onMouseEnter,
  onDoubleClick,
  onEditChange,
  onEditKeyDown,
  onCommit,
  onDateChange,
}: GridCellProps) {
  const { t } = useTranslation('knowledge');
  // A violation tints the cell rather than blocking the edit: the value is
  // already saved, and the author needs to SEE the problem to fix it. Selection
  // still wins visually so a highlighted range doesn't turn into confetti.
  const warns = violationSeverity === 'warning';
  const cls = `relative select-none border-b border-l border-border-light p-0 ${
    lastCol ? 'border-r' : ''
  } ${
    selected ? 'bg-primary/10' : violation ? (warns ? 'bg-amber-500/10' : 'bg-destructive/10') : ''
  } ${isFocus && !editing ? 'z-10 ring-2 ring-inset ring-primary' : ''}`;

  const cellHandlers = readOnly ? {} : { onMouseDown, onMouseEnter, onDoubleClick };

  // Date cells: selectable text + a calendar-icon popover that writes directly
  // (decoupled from the edit/commit flow, which was unreliable for dates).
  if (isDate) {
    return (
      <td className={cls} data-cell={dataCell} {...(readOnly ? {} : { onMouseDown, onMouseEnter })}>
        <div className="flex h-9 items-center gap-1 px-2">
          <span
            className={`min-w-0 flex-1 truncate text-sm ${value ? 'text-foreground' : 'text-muted-foreground/40'}`}
          >
            {/* A bound column has no picker, so inviting the user to pick a date
                would point at nothing. Reads like a generated column instead. */}
            {value || (isBound ? '—' : mask || t('taxonomy.fieldsEditor.pickDate'))}
          </span>
          {!readOnly && !isBound && <DatePickerButton mask={mask} onChange={onDateChange} />}
        </div>
      </td>
    );
  }

  return (
    <td className={cls} data-cell={dataCell} {...cellHandlers}>
      {editing ? (
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the input only mounts once the user starts editing, so focusing it is the interaction
          autoFocus
          value={editDraft}
          maxLength={maxLength}
          onChange={(e) => onEditChange(e.target.value)}
          onBlur={onCommit}
          onKeyDown={onEditKeyDown}
          onMouseDown={(e) => e.stopPropagation()}
          className="h-9 w-full bg-background px-2 text-sm text-foreground outline-none"
        />
      ) : (
        <div
          className={`flex h-9 items-center gap-1 truncate px-2 text-sm ${
            muted ? 'text-muted-foreground' : 'text-foreground'
          } ${!value ? 'text-muted-foreground/40' : ''}`}
        >
          <span className="min-w-0 flex-1 truncate">{value || '—'}</span>
          {violation ? (
            <ProblemTriangle message={violation} warning={warns} />
          ) : (
            /*
             * A bound cell silently refuses every edit, and without this the
             * only explanation sits on the column header, far from where the
             * user clicked. Its own trigger rather than the cell's, so the
             * cell stays a plain non-interactive element.
             *
             * Not in a read-only grid. The tooltip tells the reader to edit the
             * values in the list itself, which is advice they cannot act on here,
             * and it is one tab stop per bound cell: a 50-row shared list put
             * about a hundred of them between the grid and the controls below it,
             * all repeating one sentence.
             *
             * The per-cell trigger stays where the grid IS editable. That is
             * deliberate and tested: this table declares no `role`, so the ARIA
             * pattern puts every focusable element in the sequence - unlike the
             * record grid, which declares `role="grid"` and therefore makes its
             * own decorations `tabIndex={-1}`.
             */
            isBound &&
            !readOnly && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={t('taxonomy.fieldsEditor.cellOwnedByList')}
                      onMouseDown={(event) => event.preventDefault()}
                      className="flex shrink-0 items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Lock className="size-3 text-muted-foreground" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('taxonomy.fieldsEditor.cellOwnedByList')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          )}
        </div>
      )}
    </td>
  );
}

/** Calendar-icon button for a date cell: opens a popover calendar and writes the
 *  picked date formatted to the field's mask (e.g. "YYYY" → "2026"). Independent
 *  of the grid's edit state — picking writes straight through `onChange`. */
function DatePickerButton({
  mask,
  onChange,
}: {
  mask?: string | null;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation('knowledge');
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('taxonomy.fieldsEditor.openCalendar')}
          onMouseDown={(e) => e.stopPropagation()}
          className="shrink-0 text-muted-foreground hover:text-foreground"
        >
          <CalendarDays className="size-3.5" />
        </button>
      </PopoverTrigger>
      {/* Radix portals the content, but React synthetic events still bubble
          through the REACT tree into the grid cell's onMouseDown — which
          preventDefault()s the day click (no date written) and starts a
          drag-selection. Stop mouse/pointer events here so day clicks stay
          inside the calendar. */}
      <PopoverContent
        align="start"
        className="pointer-events-auto w-auto p-0"
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <Calendar
          mode="single"
          captionLayout="dropdown"
          onSelect={(next) => {
            if (next) {
              onChange(formatDateMask(next, mask));
              setOpen(false);
            }
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
